import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { MdDelete, MdModeEdit } from "react-icons/md";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import SummaryApi from '../common';
import BrandForm from '../components/BrandForm';
import { request } from '../helpers/request';

const AllBrands = () => {
    const [openBrandForm, setOpenBrandForm] = useState(false);
    const [brands, setBrands] = useState([]);
    const [allBrands, setAllBrands] = useState([]);
    const [editBrandData, setEditBrandData] = useState(null)
    const [search, setSearch] = useState("");

    const fetchBrands = async () => {
        try {
            const response = await request(SummaryApi.getAllBrandsAdmin);
            const responseData = await response.json();
            
            if (responseData.success) {
                setBrands(responseData.data);
                setAllBrands(responseData.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    const handleOnChange = (e) => {
        const { value } = e.target;
        setSearch(value);
        if (value) {
            const filtered = allBrands.filter(brand => brand.name.toLowerCase().includes(value.toLowerCase()));
            setBrands(filtered);
        } else {
            setBrands(allBrands);
        }
    };

    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You want to delete brand "${name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await request(SummaryApi.deleteBrand, {
                    body: JSON.stringify({ _id: id })
                });
                const responseData = await response.json();

                if (responseData.success) {
                    toast.success(responseData.message);
                    fetchBrands();
                } else {
                    toast.error(responseData.message);
                }
            } catch (error) {
                toast.error("Failed to delete brand");
            }
        }
    };

    return (
        <div className='space-y-8 pb-12'>
            {/* Header */}
            <div className='flex flex-col md:flex-row md:items-end justify-between gap-6'>
                <div className='space-y-2'>
                    <div className='flex items-center gap-3 text-accent text-xs font-black uppercase tracking-[0.2em] mb-1'>
                        <span>Catalog</span>
                    </div>
                    <h1 className='text-4xl lg:text-5xl font-black text-primary tracking-tight'>Brands</h1>
                    <p className='text-gray-500 font-medium'>Manage brands and partnerships</p>
                </div>
                <button 
                    onClick={() => {
                        setEditBrandData(null);
                        setOpenBrandForm(true);
                    }}
                    className='group flex items-center gap-3 px-8 py-4 bg-primary text-white rounded-2xl font-bold shadow-xl shadow-primary/20 hover:bg-accent transition-all active:scale-95'
                >
                    <div className='w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/30 transition-colors'>
                        <FaPlus size={10} />
                    </div>
                    <span>New Brand</span>
                </button>
            </div>

            {/* Search */}
            <div className='bg-white p-2 rounded-2xl shadow-sm border border-surface-border max-w-md flex items-center gap-3'>
                <div className='w-10 h-10 flex items-center justify-center text-gray-400'>
                    <FaSearch />
                </div>
                <input 
                    type='text' 
                    placeholder='Search brands...' 
                    className='flex-1 h-full outline-none font-medium text-primary placeholder:text-gray-400'
                    value={search}
                    onChange={handleOnChange}
                />
            </div>

            {/* Grid */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
                {brands.map((brand, index) => (
                    <div key={index} className='bg-white rounded-[2.5rem] border border-surface-border p-6 shadow-sm hover:shadow-premium transition-all group relative overflow-hidden'>
                        {/* Background Decoration */}
                        <div className='absolute -right-10 -top-10 w-40 h-40 bg-surface-muted rounded-full opacity-50 group-hover:scale-150 transition-transform duration-700' />

                        <div className='relative z-10'>
                            {/* Image & Actions */}
                            <div className='flex items-start justify-between mb-6'>
                                <div className='w-20 h-20 bg-white rounded-2xl shadow-sm border border-surface-border p-2 overflow-hidden flex items-center justify-center'>
                                    {brand.brandLogo[0] ? (
                                        <img src={brand.brandLogo[0]} alt={brand.name} className='max-w-full max-h-full object-contain' />
                                    ) : (
                                        <div className='w-full h-full bg-surface-muted flex items-center justify-center text-xs font-bold text-gray-400'>No Logo</div>
                                    )}
                                </div>
                                <div className='flex items-center gap-2'>
                                    <button 
                                        onClick={() => {
                                            setEditBrandData(brand);
                                            setOpenBrandForm(true);
                                        }}
                                        className='w-10 h-10 rounded-xl bg-surface-muted text-gray-500 hover:bg-primary hover:text-white flex items-center justify-center transition-all'
                                    >
                                        <MdModeEdit size={18}/>
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(brand._id, brand.name)}
                                        className='w-10 h-10 rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-all'
                                    >
                                        <MdDelete size={18}/>
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div>
                                <h3 className='text-xl font-black text-primary mb-1 line-clamp-1'>{brand.name}</h3>
                                <p className='text-sm text-gray-500 line-clamp-2 min-h-[40px]'>{brand.description || "No description provided."}</p>
                                
                                <div className='mt-6 pt-4 border-t border-surface-border flex items-center justify-between'>
                                    <span className='px-3 py-1 bg-surface-muted rounded-lg text-xs font-black uppercase tracking-widest text-gray-500'>
                                        {brand.productCount || 0} Products
                                    </span>
                                    <span className='text-[10px] font-bold text-gray-400'>
                                        {moment(brand.updatedAt).format('MMM D, YYYY')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {brands.length === 0 && (
                 <div className='h-64 flex flex-col items-center justify-center bg-white rounded-[2.5rem] border border-surface-border text-center p-12 shadow-premium'>
                    <h3 className='text-2xl font-black text-primary mb-2'>No Brands Found</h3>
                    <p className='text-gray-500'>Create your first brand to get started.</p>
                </div>
            )}

            {openBrandForm && (
                <BrandForm 
                    onClose={() => setOpenBrandForm(false)} 
                    brandData={editBrandData}
                    callFunc={fetchBrands}
                />
            )}
        </div>
    );
};

export default AllBrands;
